import { useEffect, useRef, useState } from 'preact/hooks';
import type { Credential } from '@/types';
import { ensureEzAuth, ensureEzAuthFacade, sharedOAuthConfig } from '@/ensureEzAuth';
import { ensureGsi, type GsiButtonOptions, type GsiCredentialResponse, type GsiIdApi } from '@/gsiLoader';
import { useStripeElementSlot } from '@/components/useStripeElementSlot';
import { logError } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';

interface Props {
  // The domain whose ezAuth Google config drives the button. When absent the
  // button renders nothing (ezAuth identity is unavailable).
  domainId?: number;
  // Resolved once the visitor completes Google sign-in: a logged-in ezAuth
  // credential the checkout flow establishes a session from. Same shape the
  // email/password path produces, so the parent handles both identically.
  onAuthenticated: (credential: Credential) => void;
  // Fired with whether the button is actually on screen (true once it renders,
  // false when it self-hides). Lets the parent show an "or" divider between this
  // and the email path only when there is a Google button to divide from.
  onAvailabilityChange?: (available: boolean) => void;
}

// GIS button width must be an integer; clamp the measured slot width into
// Google's supported range, falling back to a sensible default before layout.
const GSI_BUTTON_MIN_WIDTH = 200;
const GSI_BUTTON_MAX_WIDTH = 400;
const GSI_BUTTON_DEFAULT_WIDTH = 320;

const GSI_BUTTON_OPTIONS: GsiButtonOptions = {
  type: 'standard',
  theme: 'outline',
  size: 'large',
  text: 'continue_with',
  shape: 'rectangular',
  // Center the logo+label so the button matches the centered "Log in" /
  // "Create account" buttons it sits with, rather than hugging the left edge.
  logo_alignment: 'center',
};

// Until config resolves the button is hidden so it never flashes empty space on
// domains without Google enabled; 'ready' means GIS is initialized and the
// button can be rendered into its slotted mount.
type Status = 'pending' | 'ready' | 'hidden';

// GoogleSignInButton renders ezAuth's "Continue with Google" option when the
// domain has Google enabled, drives the Google Identity Services flow, and
// resolves the resulting ezAuth session as a credential. It is ezAuth-only: the
// caller does not render it in BYO mode. It self-hides (renders nothing) when
// Google is not configured, the domain is missing, or setup fails, so the
// email/password path is always still available.
export function GoogleSignInButton({ domainId, onAuthenticated, onAvailabilityChange }: Props) {
  useT();
  const [status, setStatus] = useState<Status>('pending');
  const [error, setError] = useState<string | null>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  // The initialized GIS API, set once config resolves; the slotted mount reads
  // it to render the button, and teardown cancels any open credential UI through it.
  const gsiRef = useRef<GsiIdApi | null>(null);

  useEffect(() => {
    let active = true;

    // handleCredential completes the Google login: exchange the id_token for an
    // ezAuth session, then resolve the access token as a credential. Never
    // throws; a failure surfaces a generic, retryable message.
    const handleCredential = (response: GsiCredentialResponse): void => {
      if (!active || !response || typeof response.credential !== 'string' || response.credential === '') {
        return;
      }
      void (async () => {
        setError(null);
        try {
          if (typeof domainId !== 'number') {
            return;
          }
          const facade = await ensureEzAuthFacade(domainId);
          if (!active || !facade) {
            return;
          }
          await facade.loginWithGoogleCredential(response.credential);
          const token = await ensureEzAuth(domainId);
          if (!active) {
            return;
          }
          if (token) {
            onAuthenticated({ ezAuthToken: token });
            return;
          }
          setError(t('googleSignIn.error'));
        } catch (err) {
          logError('Google sign-in failed', err);
          if (active) {
            setError(t('googleSignIn.error'));
          }
        }
      })();
    };

    void (async () => {
      if (typeof domainId !== 'number') {
        if (active) {
          setStatus('hidden');
        }
        return;
      }

      const facade = await ensureEzAuthFacade(domainId);
      if (!active) {
        return;
      }
      if (!facade) {
        setStatus('hidden');
        return;
      }

      try {
        const config = await sharedOAuthConfig(facade);
        const clientId = config.google.enabled ? config.google.clientId : undefined;
        if (!active) {
          return;
        }
        if (!clientId) {
          setStatus('hidden');
          return;
        }
        const nonce = await facade.getOAuthNonce();
        const api = await ensureGsi();
        if (!active) {
          return;
        }
        api.initialize({
          client_id: clientId,
          nonce,
          ux_mode: 'popup',
          itp_support: true,
          callback: handleCredential,
        });
        gsiRef.current = api;
        setStatus('ready');
      } catch (err) {
        // Google is configured but its setup failed; hide the button rather than
        // block the email/password path that is still mounted alongside it.
        logError('Google sign-in setup failed; Google is disabled here', err);
        if (active) {
          setStatus('hidden');
        }
      }
    })();

    return () => {
      active = false;
      // Drop any open One Tap/credential UI tied to this mount.
      gsiRef.current?.cancel();
    };
  }, [domainId]);

  // GIS draws its button as an iframe that can't render inside the widget's
  // shadow root (the same limitation Stripe Elements has), so reuse the shared
  // slotted light-DOM mount to project it into the in-flow slot.
  const slotName = useStripeElementSlot(
    slotRef,
    async (mountDiv) => {
      const api = gsiRef.current;
      if (status !== 'ready' || !api) {
        return;
      }
      api.renderButton(mountDiv, { ...GSI_BUTTON_OPTIONS, width: measureButtonWidth(slotRef.current) });
    },
    [status, domainId],
  );

  // Tell the parent whether a Google button is on screen so it can gate the
  // "or" divider. 'pending' stays silent (default false) so the divider never
  // flashes before config resolves.
  useEffect(() => {
    if (status === 'ready') {
      onAvailabilityChange?.(true);
    } else if (status === 'hidden') {
      onAvailabilityChange?.(false);
    }
  }, [status, onAvailabilityChange]);

  if (status === 'hidden') {
    return null;
  }

  return (
    <div class="sm-google">
      <div class="sm-google-button" ref={slotRef}><slot name={slotName} /></div>
      {error && <p class="sm-error" role="alert">{error}</p>}
    </div>
  );
}

function measureButtonWidth(target: HTMLElement | null): number {
  const measured = target ? target.getBoundingClientRect().width || target.offsetWidth : 0;
  const floored = Math.floor(measured) || GSI_BUTTON_DEFAULT_WIDTH;
  return Math.min(GSI_BUTTON_MAX_WIDTH, Math.max(GSI_BUTTON_MIN_WIDTH, floored));
}
